<template>
  <!--  min-width: 300px, please enforce in container-->
  <b-col :class="['dict-page', hideInlineSearch ? 'dict-page--navbar-search' : 'mt-5']">
    <div class="ga-dict-content-band ga-dict-content-band--table">
    <div class="dict-search-wrap mt-2" v-if="!hideInlineSearch">
      <v-select
        :filterable="false"
        :clearable="false"
        taggable
        :create-option="word => ({'code': { 'c': word, info: $t(' - gender app search')} })"
        :getOptionLabel="option => option.code.c"
        :options="autocompletionOptions"
        @input="newSearchTerm(searchTerm.code.c)"
        @search="setAutocompletionOptions"
        v-model="searchTerm">
        <template #option="{ code, label}">
          <div class="dict-select-option">
            <span class="dict-select-option__term">{{ code.c }}</span>
            <span class="dict-select-option__hint" v-if="code.info">{{ code.info }}</span>
            <span class="dict-select-option__alt" v-if="label">{{ label }}</span>
          </div>
        </template>
        <template slot="no-options">
          <div class="dict-select-loading" v-if="autocompleteloading">
            <b-spinner small variant="secondary"></b-spinner>
            <span>{{ $t('dict.searching') }}</span>
          </div>
        </template>
      </v-select>
    </div>

    <div class="dict-toolbar dict-toolbar--sort-only" v-if="!hideInlineSearch">
      <b-dropdown
        variant="link"
        toggle-class="dict-sort-menu__toggle"
        menu-class="dict-sort-menu__menu"
        no-caret
        :aria-label="$t('dict.sort_by')"
      >
        <template #button-content>
          <span class="dict-sort-menu__prefix">{{ $t('dict.sort_by') }}</span>
          <span class="dict-sort-menu__value">{{ sortLabel }}</span>
          <i class="fas fa-chevron-down dict-sort-menu__chevron" aria-hidden="true"></i>
        </template>
        <b-dropdown-item
          v-for="option in sortOptions"
          :key="option.value"
          :active="dictionaryEntriesOrdering === option.value"
          @click="changeDictionaryEntriesOrdering(option.value)"
        >{{ option.label }}</b-dropdown-item>
      </b-dropdown>
    </div>

    <div
      class="dict-inline-hint"
      v-if="neutralEntryInfo && loadedDictionaryEntries.length > 0"
    >
      <p>{{ neutralEntryInfo }}</p>
      <p v-if="callToAction" class="dict-inline-hint__sub">{{ callToAction }}</p>
    </div>

    <div
      class="dict-empty"
      v-if="loadedDictionaryEntries.length <= 0 && isApiCalled"
    >
      <p class="dict-empty__title">{{ neutralEntryInfo || $t('No entry yet') }}</p>
      <p class="dict-empty__text" v-if="callToAction">{{ callToAction }}</p>
      <div class="dict-empty__actions">
        <button
          type="button"
          class="dict-empty__btn dict-empty__btn--primary"
          v-on="isLoggedIn() ? { click: () => add('') } : { click: () => showRegisterModal() }"
        >{{ $t('Add Term') }}</button>
        <router-link :to="{ name: 'dictionary.helfen' }" class="dict-empty__btn dict-empty__btn--ghost">
          {{ $t('helfen.cta') }}
        </router-link>
      </div>
    </div>

    <dict-entry-table
      v-if="loadedDictionaryEntries.length > 0"
      :class="['dict-list', hideInlineSearch ? 'dict-list--navbar' : 'mt-1 mb-md-3 py-1']"
      :entries="loadedDictionaryEntries"
      :parent-search-term="searchTerm"
      :shortform-placeholder="shortformPlaceholder"
      :shortform-placeholder-profile="shortformPlaceholderProfile"
      @like="like"
      @dislike="dislike"
      @add="add"
      @flag="flag"
      @register="showRegisterModal"
    />

    <div class="p-0" v-if="!isApiCalled">
      <LoadingPlaceholder class="fade-out p-0"></LoadingPlaceholder>
    </div>

    <scroll-loader :loader-disable="disableLoadingNextPage" :loader-method="fetchAndSetNextPage">
      <div class="dict-scroll-loader">
        <b-spinner small variant="secondary"></b-spinner>
      </div>
    </scroll-loader>
    </div>

    <LoginWallModal
      ref="registerModal"
      :hint="$t('login_wall.dict_hint')"
    />
  </b-col>
</template>

<script>
import { DictionaryEntryService } from '../services/dictionaryEntryService'
import LoadingPlaceholder from './LoadingPlaceholder'
import DictEntryTable from './DictEntryTable'
import LoginWallModal from './LoginWallModal'
import { modalMixin } from '../mixins/modalMixin'
import { dictionaryEntryMixin } from '../mixins/dictionaryEntryMixin'
import { compareMixin } from '../mixins/compareMixin'
import { listMixin } from '../mixins/listMixin'
import { ProfileDictionaryEntryService } from '@/services/profileDictionaryEntryService'
import { dictionarySortBus } from '@/utils/dictionarySortBus'
import { resolveApiUrl } from '@/env'

var Vue = require('vue')
export default {
  name: 'List',
  // insert the following code for vue-meta to work
  metaInfo () {
    if (this.$route.params.specific) {
      return {
        title: this.title,
        meta: [
          {
            name: 'description',
            vmid: 'description',
            content: this.description
          },
          {
            property: 'og:title',
            vmid: 'og:title',
            content: this.title
          },
          {
            property: 'og:description',
            vmid: 'og:description',
            content: this.description
          },
          {
            property: 'og:url',
            vmid: 'og:url',
            content: this.canonicalUrl
          },
          {
            name: 'twitter:title',
            vmid: 'twitter:title',
            content: this.title
          },
          {
            name: 'twitter:description',
            vmid: 'twitter:description',
            content: this.description
          }
        ],
        link: [
          { rel: 'canonical', href: this.canonicalUrl },
          { rel: 'alternate', hreflang: 'de', href: this.canonicalUrl }
        ]
      }
    }
  },
  components: {
    LoadingPlaceholder,
    DictEntryTable,
    LoginWallModal
  },
  data () {
    return {
      userData: {},
      loadedDictionaryEntries: [],
      disableLoadingNextPage: false,
      searchTerm: '', // Hint: When auto completion list is open, the search term is in searchterm.code.c
      nextPageApi: '',
      neutralEntryInfo: '',
      callToAction: '',
      autocompleteloading: false,
      description: '',
      secondTierLoadingDone: false,
      shortformPlaceholder: '',
      canonicalUrl: '',
      page: 1,
      pageSize: 9999,
      autocompletionOptions: [],
      isPaginated: false,
      isApiCalled: false,
      dictionaryEntriesOrdering: 'top'
    }
  },
  computed: {
    title: function () {
      return `${this.$route.params.specific} ${'| gender app · Alternativen finden & erfassen'.slice(0, 60 - 3 - this.$route.params.specific.length)}${60 - 3 - this.$route.params.specific.length < 45 ? '...' : ''}`
    },
    sortOptions () {
      return [
        { value: 'top', label: this.$t('dict.sort_top') },
        { value: 'new', label: this.$t('dict.sort_new') },
        { value: 'alpha', label: this.$t('dict.sort_alpha') },
        { value: 'omega', label: this.$t('dict.sort_omega') }
      ]
    },
    sortLabel () {
      const match = this.sortOptions.find(o => o.value === this.dictionaryEntriesOrdering)
      return match ? match.label : this.$t('dict.sort_top')
    }
  },
  props: ['filterBy', 'shortformPlaceholderProfile', 'hideInlineSearch'],
  mixins: [modalMixin, dictionaryEntryMixin, compareMixin, listMixin],
  methods: {
    shortFormLabel (type, shortformPlaceholderKey) {
      if (type === 'neut') return ''
      if (type === 'pair') return 'pair'
      const relevantPlaceholderKey = this.shortformPlaceholderProfile ? this.shortformPlaceholderProfile : shortformPlaceholderKey
      if (!relevantPlaceholderKey.trim()) return 'star'
      return relevantPlaceholderKey
    },
    placeholderForView (shortformPlaceholderKey) {
      const relevantPlaceholderKey = this.shortformPlaceholderProfile ? this.shortformPlaceholderProfile : shortformPlaceholderKey
      let viewable = '*'
      if (relevantPlaceholderKey === 'star') {
        viewable = '*'
      } else if (relevantPlaceholderKey === 'colon') {
        viewable = ':'
      } else if (relevantPlaceholderKey === 'lowdash') {
        viewable = '_'
      } else if (relevantPlaceholderKey === 'slash') {
        viewable = '/-'
      }
      return viewable
    },
    fixShortformPlaceholder (entry) {
      return entry.replace('*', this.placeholderForView(this.shortformPlaceholder))
    },
    getUsername () {
      return Vue.default.cookie.get('username', 'Profil')
    },
    changeDictionaryEntriesOrdering (dictionaryEntriesOrdering) {
      this.dictionaryEntriesOrdering = dictionaryEntriesOrdering
      dictionarySortBus.$emit('sort-state', dictionaryEntriesOrdering)
      this.newSearchTerm()
    },
    onExternalSortChange (ordering) {
      if (ordering !== this.dictionaryEntriesOrdering) {
        this.changeDictionaryEntriesOrdering(ordering)
      }
    },
    newSearchTerm (searchTerm, userFilter, dictionaryEntriesOrdering, validated = false) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      })
      const instance = this
      // interface for displaying cards after search term changes
      // arguments are optional
      // results in the list being shown, the auto completion options being updated and a scroll up event

      // if the search term is already set, no need to reload
      if (searchTerm && (instance.$route.query.search === searchTerm || instance.$route.params.specific === searchTerm)) {
        instance.scrollUp()
        return
      }
      const term = searchTerm || this.$route.params.specific || this.$route.query.search || ''
      const filter = userFilter || this.filterBy || 'all'
      const ordering = dictionaryEntriesOrdering || this.dictionaryEntriesOrdering || 'top'
      this.setUrl(term, validated).then(() => {
        // the set URL is used in fetchAndSetFirstPage()
        // Hint: if term is entry, url is not set, so we don't overwrite existing URLs
        Promise.all(
          [instance.fetchAndSetFirstPage(term, filter, ordering),
            instance.setSearchTerm(term),
            instance.scrollUp()
          ]
        ).then(() => {
          Promise.all(
            [instance.setAutocompletionOptions(term),
              instance.updateUrl(term),
              instance.setDescription(term)
            ]
          )
        }).then(() => {
          this.secondTierLoadingDone = true
          this.$emit('secondTierLoadingDone', true)
        }
        )
      })
    },
    removeDuplicates (myArr, prop) {
      return myArr.filter((obj, pos, arr) => {
        return arr.map(mapObj => mapObj[prop]).indexOf(obj[prop]) === pos
      })
    },
    fetchAndSetNextPage () {
      // requires fetchAndSetFirstPage to set nextPageApi first
      // Known issue: Will not run if scroll event is not triggered
      const instance = this
      instance.disableLoadingNextPage = !instance.nextPageApi
      if (instance.disableLoadingNextPage) return
      DictionaryEntryService.getPaginatedData(instance.nextPageApi)
        .then((response) => {
          instance.loadedDictionaryEntries = [...instance.loadedDictionaryEntries, ...response.data.results]
          // insert this code to ensure unique dictionary entry in list view
          // if need ordering in the backend is not unambiguous
          // hint: class DictionaryEntryHomeView(BaseModelViewSet): ........order_by('-net_likes', 'pk')
          // const uniqueArray = this.removeDuplicates(newArray, 'id')
          // console.log(instance.loadedDictionaryEntries)
          // instance.loadedDictionaryEntries = [...uniqueArray]
          instance.nextPageApi = resolveApiUrl(response.data.next)
          // Stop scroll loading...
          instance.disableLoadingNextPage = response.data.remaining_count <= 0
        })
        .catch((error) => {
          console.log(error)
          instance.disableLoadingNextPage = false
        })
    },
    async setAutocompletionOptions (searchArg) {
      const instance = this
      const search = searchArg || (typeof (this.searchTerm) === 'string' ? this.searchTerm : '') || (this.searchTerm.code && this.searchTerm.code.c) || ''
      if (this.hideInlineSearch || !search.trim()) {
        instance.autocompleteloading = false
        return
      }
      this.autocompletionOptions = []
      instance.autocompleteloading = true
      this.$forceUpdate()
      DictionaryEntryService.getAutoCompletionData(search.trim()).then((response) => {
        const datasets = response.data.results
        const unsorted = []
        for (const data of datasets) {
          const _option = this.prepareAutoCompleteOptions(data)
          unsorted.push(..._option)
        }
        const unique = []
        const map = new Map()
        for (const item of unsorted) {
          if (!map.has(item.code.c)) {
            map.set(item.code.c, true) // set any value to Map
            unique.push({
              code: item.code,
              label: item.label,
              id: item.id
            })
          }
        }
        this.autocompletionOptions = unique.sort(function (a, b) {
          // return 0
          // words similar to the search words will show up un top of the search.
          if (!a.code || !b.code || !search) return 0
          if (!a.code.c || !b.code.c) return 0
          return instance.jaroWinkerStringSimilarity(b.code.c, search) - instance.jaroWinkerStringSimilarity(a.code.c, search)
        })
      }).catch((error) => {
        console.log(error)
      }).then(() => {
        instance.autocompleteloading = false
        this.$forceUpdate()
      }
      ).then(() => {
        if (!search && !this.hideInlineSearch) {
          const searchElement = this.$el.querySelector('.dict-search-wrap .vs__search')
          if (searchElement) {
            searchElement.setAttribute('placeholder', this.$t('search...'))
          }
        }
      })
    },
    //  for the other methods, check relevant mixins
    setDescription (searchTerm) {
      let _canonicalUrl = 'https://genderapp.org/'
      let _description = 'Geschlechtergerecht:'
      if (this.loadedDictionaryEntries.length > 0) {
        const topAnswer = this.loadedDictionaryEntries[0]
        _canonicalUrl += 'dict/'
        const canonicalCandidates = [topAnswer.singular_masculine, topAnswer.term, topAnswer.plural_masculine, topAnswer.singular_feminine, topAnswer.plural_feminine]
        const match = canonicalCandidates.find(el => el === searchTerm)
        if (match) {
          this.canonicalUrl = encodeURI(_canonicalUrl + canonicalCandidates.find(el => el))
        }
        if ((searchTerm === topAnswer.plural_masculine || searchTerm === topAnswer.plural_feminine) && topAnswer.plural_gender_neutral) {
          _description += ` '${topAnswer.plural_gender_neutral}';`
        } else if ((searchTerm === topAnswer.singular_masculine || searchTerm === topAnswer.singular_feminine) && topAnswer.singular_gender_neutral) {
          _description += ` '${topAnswer.singular_gender_neutral}';`
        } else if ((searchTerm === topAnswer.term) && topAnswer.alternative) {
          _description += ` '${topAnswer.alternative}';`
        }
      }
      if (this.loadedDictionaryEntries.length > 1) {
        const secondAnswer = this.loadedDictionaryEntries[1]
        if ((searchTerm === secondAnswer.plural_masculine || searchTerm === secondAnswer.plural_feminine) && secondAnswer.plural_gender_neutral) {
          _description += ` '${secondAnswer.plural_gender_neutral}';`
        } else if ((searchTerm === secondAnswer.singular_masculine || searchTerm === secondAnswer.singular_feminine) && secondAnswer.singular_gender_neutral) {
          _description += ` '${secondAnswer.singular_gender_neutral}';`
        } else if ((searchTerm === secondAnswer.term) && secondAnswer.alternative) {
          _description += ` '${secondAnswer.alternative}';`
        }
      }
      if (_description === 'Geschlechtergerecht:') {
        this.description = `Geschlechtergerechte Alternativen für '${searchTerm}' im demokratischen Wörterbuch von gender app`
        return this.description
      } else {
        _description += ' ... gender app · Gendern mit demokratischem Wörterbuch und Office Add-ins'
      }
      this.description = _description
      return _description
    },
    async getShortformPlaceholder () {
      if (!this.getUsername()) return
      await ProfileDictionaryEntryService.getProfileDictionaryEntryStatistics(this.getUsername())
        .then((response) => {
          this.shortformPlaceholder = response.data.shortform_placeholder
        }).catch((error) => {
          console.log(error)
        })
    }
  },
  created () {
  },
  mounted () {
    dictionarySortBus.$on('sort-change', this.onExternalSortChange)
    dictionarySortBus.$emit('sort-state', this.dictionaryEntriesOrdering)
    this.getShortformPlaceholder()
    // Hint: url is read in 'newSearchTerm'. That way we make sure that every 'mounted' the cards are reloaded
    this.newSearchTerm('', '', '')
    // Hint: autocompletion is already update in newSearchTerm, so no need to do it here.
  },
  beforeDestroy () {
    dictionarySortBus.$off('sort-change', this.onExternalSortChange)
  },
  watch: {
    $route (to, from) {
      this.newSearchTerm('', '', '')
    },
    filterBy: function () {
      this.newSearchTerm('', this.filterBy, 'top')
    }
  }
}
</script>

<style scoped>

  .card-body {
    padding: 0.5rem;
  }

  .list-group-item {
    border: transparent;
  }

  /*.invisible-scrollbar {*/
  /*  scrollbar-width: none;*/
  /*}*/

  .invisible-scrollbar::-webkit-scrollbar {
    /*Chrome*/
    display: none !important;
  }
  .invisible-scrollbar {
  /*FireFox*/
  scrollbar-width: none !important;
  /*IE10+*/
  -ms-overflow-style: -ms-autohiding-scrollbar;
  }

  .please-scroll {
    white-space: nowrap;
    overflow: hidden;
    overflow-x: scroll;
  }

  .center-left {
    position: absolute;
    top: 50%;
    left: 60%;
    transform: translate(-50%, -50%);
  }

  .center-right {
    font-size: larger;
    position: absolute;
    top: 50%;
    left: 40%;
    transform: translate(-50%, -50%);
  }

@keyframes shake-for-attention {
    11% {
        transform: rotate(90deg);
    }
    22% {
        transform: rotate(0deg);
    }}

  .rotate-text {
    top: 50%;
    left: 50%;
    width: 10px;
    height: 10px;
    margin: 0;
    -webkit-animation: spin 0.2s linear infinite;
    -moz-animation: spin 2s linear infinite;
    animation: spin 2s linear infinite;
  }

  .rotate-pause {
    -webkit-animation: shake-for-attention 5s linear infinite;
    -moz-animation: shake-for-attention 5s linear infinite;
    animation: shake-for-attention 5s linear infinite;
  }

  @-moz-keyframes spin {
    100% {
      -moz-transform: rotateY(360deg);
    }
  }

  @-webkit-keyframes spin {
    100% {
      -webkit-transform: rotateY(360deg);
    }
  }

  @keyframes spin {
    100% {
      -webkit-transform: rotateY(360deg);
      transform: rotateY(360deg);
    }
  }
  .gender_card_muted {
    background-color: #c6d4e2;
  }

  .gender_card:hover {
    -webkit-filter: brightness(1.02);
    -moz-filter: brightness(1.02);
    -ms-filter: brightness(1.02);
    filter: brightness(1.02);
    -webkit-box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.3);
    -moz-box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.3);
    box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.3);
  }

  .gender_card:focus {
    -webkit-filter: brightness(1.02);
    -moz-filter: brightness(1.02);
    -ms-filter: brightness(1.02);
    filter: brightness(1.02);
    -webkit-box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.3);
    -moz-box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.3);
    box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.3);
  }

  .gender_card:active {
    -webkit-filter: brightness(1.02);
    -moz-filter: brightness(1.02);
    -ms-filter: brightness(1.02);
    filter: brightness(1.02);
    -webkit-box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.3);
    -moz-box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.3);
    box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.3);
  }

  .fade-out {
    animation: Pulsate 4s linear infinite;
  }

  @keyframes Pulsate {
    from {
      opacity: 1;
    }
    50% {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
</style>
